import { useEffect, useState } from 'react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { getReleaseCheck } from '@/api/client';
import type { ReleaseCheckDto, ReleaseOverrideDto } from '@/api/types';

/**
 * RELEASING AN OUTBOUND LOAD PAST ITS WARNINGS (#97, design §3).
 *
 * ⭐ THIS DIALOG IS THE CONTROL. Not the button behind it — the control is that
 * a named human read these warnings, said in writing why the truck may go, and
 * that the answer is on the appointment forever. A hard block would strand real
 * trucks at real gates over middle initials, and the scheduler standing there
 * can see things the database cannot.
 *
 * ⚠️ IT RE-READS THE CHECK FROM THE SERVER rather than rendering whatever came
 * back on the 409. Between the click and this dialog opening, somebody may have
 * scanned the licence at the next desk — showing a stale list would have the
 * scheduler sign off on a problem that no longer exists, which devalues every
 * signature after it.
 *
 * ⛔ AND THE SERVER RE-JUDGES ANYWAY. Nothing sent from here is trusted as a
 * statement of what was wrong; the acknowledgement only records what this person
 * was shown and agreed to.
 */

interface Props {
    open: boolean;
    appointmentId: number | null;
    appointmentNumber: string | null;
    getToken: () => Promise<string>;
    /** True while the parent is running the release. */
    busy?: boolean;
    onCancel: () => void;
    onConfirm: (override: ReleaseOverrideDto) => void;
}

export function OutboundReleaseDialog({
    open, appointmentId, appointmentNumber, getToken, busy = false, onCancel, onConfirm,
}: Props) {
    const [check, setCheck] = useState<ReleaseCheckDto | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [reason, setReason] = useState('');
    /**
     * ⛔ ONE TICK PER FINDING, NOT ONE FOR ALL OF THEM. A single "I confirm"
     * button lets a finding the scheduler never read ride along on the same
     * click — and it is precisely the unread one that matters. Ticking them
     * individually is what makes the log line mean something.
     */
    const [ticked, setTicked] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (!open || appointmentId === null) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        setReason('');
        setTicked(new Set());
        setCheck(null);
        void (async () => {
            try {
                const res = await getReleaseCheck(await getToken(), appointmentId);
                if (!cancelled) setCheck(res);
            } catch {
                if (!cancelled) {
                    // ⚠️ NO SILENT RELEASE. If the check cannot be read, the
                    // dialog says so and offers nothing to click — the server
                    // would refuse anyway, and a dialog that let the scheduler
                    // proceed here would be teaching him the check is optional.
                    setError('Could not read the release check. Try again — the appointment cannot be '
                           + 'released until it succeeds.');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open, appointmentId, getToken]);

    const must = check?.mustAcknowledge ?? [];
    const allTicked = must.length > 0 && must.every(c => ticked.has(c));
    const reasonOk = reason.trim().length >= 3;
    const canRelease = !busy && !loading && !error && allTicked && reasonOk;

    const toggle = (code: string) => {
        setTicked(prev => {
            const next = new Set(prev);
            if (next.has(code)) next.delete(code); else next.add(code);
            return next;
        });
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && !busy && onCancel()}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>Release this outbound truck?</DialogTitle>
                    <DialogDescription>
                        {appointmentNumber ?? check?.appointmentNumber ?? 'This appointment'} has warnings that
                        have to be acknowledged before the truck leaves.
                        {check?.comparedAgainst && (
                            <span className="block">
                                The license was compared against <strong>{check.comparedAgainst}</strong>.
                            </span>
                        )}
                    </DialogDescription>
                </DialogHeader>

                {loading && <div className="text-xs text-muted-foreground">Checking…</div>}

                {error && (
                    <div className="rounded border border-destructive/40 bg-destructive/5 px-2 py-1 text-xs text-destructive">
                        {error}
                    </div>
                )}

                {check && !error && (
                    <div className="space-y-2">
                        {/**
                          * ⭐⭐ THE TICKS NOW SAY WHAT THEY ARE FOR (2026-08-27).
                          *
                          * Pete, looking at this dialog: *"Not sure what the Check box
                          * does here either?"*
                          *
                          * ⛔ AND THAT WAS A FAIR QUESTION — THE BOX HAD NO LABEL OF ITS
                          * OWN. Each row rendered the FINDING's text beside a checkbox
                          * and nothing anywhere said what ticking it meant. It reads as a
                          * setting ("is this true?", "ignore this?") when it is actually a
                          * signature: *I have looked at the card myself.*
                          *
                          * ⚠️ A CONTROL WHOSE PURPOSE HAS TO BE GUESSED GETS TICKED
                          * ANYWAY, which is the one outcome an acknowledgement must not
                          * have. The header below costs a line and removes the guess.
                          */}
                        {check.findings.some(f => f.requiresOverride) && (
                            <div className="text-xs font-medium">
                                {/* ⚠️ THE WORDING HAD TO WIDEN. It used to say "against the
                                    card in your hand", which is the right instruction for a
                                    licence finding and the wrong one for a missing packing
                                    slip — and a scheduler following it literally would tick
                                    a paperwork gap after looking at a driving licence. */}
                                Tick each one to confirm you have dealt with it:
                            </div>
                        )}
                        {/* Override-worthy findings, each with its own tick. */}
                        {check.findings.filter(f => f.requiresOverride).map(f => (
                            <label
                                key={f.code}
                                className="flex cursor-pointer items-start gap-2 rounded border border-amber-400
                                           bg-amber-50 px-2 py-1.5 text-xs text-amber-900"
                            >
                                <input
                                    type="checkbox"
                                    className="mt-0.5"
                                    checked={ticked.has(f.code)}
                                    disabled={busy}
                                    onChange={() => toggle(f.code)}
                                />
                                <span>
                                    {/**
                                      * ⭐ #317 — THE TWO SOURCES LOOK THE SAME AND ARE
                                      * NOT. A licence finding is answered by looking at
                                      * the card in the driver's hand; a checklist one is
                                      * answered by fetching a piece of paper. The header
                                      * above says "check it against the card", which is
                                      * simply wrong instruction for half of these now.
                                      *
                                      * ⚠️ A WORD RATHER THAN A SECOND SECTION. Splitting
                                      * them would give this dialog two tick lists and two
                                      * places to miss one — and the whole point of #317
                                      * step 4 was to use the gate that already exists
                                      * rather than build a second.
                                      */}
                                    {f.source === 'checklist' && (
                                        <span className="mr-1 rounded bg-amber-200 px-1 text-[10px] font-semibold uppercase">
                                            paperwork
                                        </span>
                                    )}
                                    {f.message}
                                </span>
                            </label>
                        ))}

                        {/* Findings worth seeing that do not need a signature. Shown
                            below the ticks so they cannot be mistaken for them.
                            ⭐ And now labelled too, for the same reason as the ticks:
                            an un-ticked grey box beside ticked amber ones invites
                            "did I miss one?" */}
                        {check.findings.some(f => !f.requiresOverride) && (
                            <div className="text-xs text-muted-foreground">
                                For information — nothing to tick:
                            </div>
                        )}
                        {check.findings.filter(f => !f.requiresOverride).map(f => (
                            <div
                                key={f.code}
                                className="rounded border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-700"
                            >
                                {f.message}
                            </div>
                        ))}

                        {must.length === 0 && (
                            // The problem cleared while the dialog was opening —
                            // somebody scanned the licence at the next desk.
                            <div className="rounded border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs text-emerald-900">
                                Nothing needs acknowledging any more — this appointment can be released
                                normally. Close this and press Depart again.
                            </div>
                        )}

                        {must.length > 0 && (
                            <div className="space-y-1.5">
                                <Label htmlFor="release-reason">
                                    Why is this appointment being released? (required)
                                </Label>
                                <Textarea
                                    id="release-reason"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    placeholder="e.g. checked his CDL by hand against the card, expiry is 2027"
                                    rows={3}
                                    disabled={busy}
                                />
                                {/* ⚠️ Said plainly. A scheduler who does not realise this
                                    is permanent will write "ok" in it, and then the
                                    record is worse than nothing — it looks like
                                    diligence and carries none. */}
                                <div className="text-[11px] text-muted-foreground">
                                    This is written to {check.appointmentNumber ?? 'the appointment'}'s
                                    history with your name against it, permanently.
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <DialogFooter>
                    <div className="flex w-full justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>
                            Don't release
                        </Button>
                        <Button
                            size="sm"
                            disabled={!canRelease}
                            onClick={() => onConfirm({
                                acknowledged: [...ticked],
                                overrideReason: reason.trim(),
                            })}
                        >
                            {busy ? 'Releasing…' : 'Release the truck'}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
